import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Wallet, Receipt, Trash2 } from 'lucide-react';
import api from '@/lib/api';
import { formatRp, formatDate, todayISO, METODE_OPTIONS, TIPE_OPTIONS } from '@/lib/format';
import { PaymentBadge, StageBadge } from '@/components/StatusBadge';
import { ListSkeleton } from '@/components/Skeletons';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

export default function Pembayaran() {
  const [rec, setRec] = useState({ total_piutang: 0, items: [] });
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ project_id: '', jumlah: '', metode: 'transfer', tipe: 'dp', tanggal: todayISO(), catatan: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([api.get('/receivables'), api.get('/projects')]);
      setRec(r.data); setProjects(p.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = (projectId) => {
    setForm({ project_id: projectId || '', jumlah: '', metode: 'transfer', tipe: 'dp', tanggal: todayISO(), catatan: '' });
    setOpen(true);
  };

  const save = async () => {
    if (!form.project_id) { toast.error('Pilih proyek dulu'); return; }
    if (!Number(form.jumlah)) { toast.error('Isi jumlah pembayaran'); return; }
    setSaving(true);
    try {
      await api.post('/payments', { ...form, jumlah: Number(form.jumlah) });
      toast.success('Pembayaran dicatat');
      setOpen(false); load();
    } catch (e) { !e?.__gated && toast.error(e?.response?.data?.detail || 'Gagal mencatat'); } finally { setSaving(false); }
  };

  const selectedProject = projects.find((p) => p.id === form.project_id);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-xl font-bold">Pembayaran & Piutang</h1>
          <p className="text-sm text-muted-foreground">Catat DP, progress, & pelunasan.</p>
        </div>
        <Button data-testid="pembayaran-catat-button" onClick={() => openAdd('')} className="rounded-xl"><Plus className="mr-1.5 h-4 w-4" /> Catat</Button>
      </div>

      <div className="rounded-2xl border bg-card p-5" style={{ backgroundImage: 'radial-gradient(600px 280px at 100% 0%, rgba(251,146,60,0.10), transparent 60%)' }}>
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Wallet className="h-4 w-4" /> Total Piutang (belum lunas)</div>
        <div data-testid="pembayaran-total-piutang" className="mt-1 font-display text-3xl font-bold text-orange-600 tabular-nums">{formatRp(rec.total_piutang)}</div>
      </div>

      <h2 className="font-display text-sm font-semibold">Daftar Proyek & Tagihan</h2>
      {loading ? <ListSkeleton /> : rec.items.length === 0 ? (
        <EmptyState icon={Receipt} title="Belum ada proyek" description="Konversi penawaran jadi proyek untuk mulai mencatat pembayaran." />
      ) : (
        <div className="space-y-2.5">
          {rec.items.map((it) => {
            const pct = it.nilai > 0 ? Math.min(100, Math.round((it.sudah_dibayar / it.nilai) * 100)) : 0;
            return (
              <div key={it.project_id} className="rounded-xl border bg-card p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-semibold">{it.nama_proyek}</div>
                    <div className="truncate text-xs text-muted-foreground">{it.customer_name}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <PaymentBadge status={it.status} />
                    <StageBadge stage={it.stage} />
                  </div>
                </div>
                <Progress value={pct} className="mt-3 h-2" />
                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Dibayar <b className="text-foreground">{formatRp(it.sudah_dibayar)}</b> dari {formatRp(it.nilai)}</span>
                  {it.sisa > 0 ? <span className="font-semibold text-orange-600">Sisa {formatRp(it.sisa)}</span> : <span className="font-semibold text-emerald-600">Lunas</span>}
                </div>
                {it.sisa > 0 && (
                  <Button variant="outline" size="sm" onClick={() => openAdd(it.project_id)} className="mt-3 rounded-lg">Catat Pembayaran</Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Catat Pembayaran</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Proyek *</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="pembayaran-form-proyek"><SelectValue placeholder="Pilih proyek" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama_proyek} — {p.customer_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {selectedProject && (
              <div className="rounded-lg bg-muted/50 p-2.5 text-xs text-muted-foreground">
                Nilai: <b className="text-foreground">{formatRp(selectedProject.nilai)}</b> · Sisa: <b className="text-orange-600">{formatRp(selectedProject.sisa)}</b>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Jumlah (Rp) *</Label><Input data-testid="pembayaran-form-jumlah" type="number" value={form.jumlah} onChange={(e) => setForm({ ...form, jumlah: e.target.value })} placeholder="0" /></div>
              <div className="space-y-1.5"><Label>Tanggal</Label><Input type="date" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Jenis</Label>
                <Select value={form.tipe} onValueChange={(v) => setForm({ ...form, tipe: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPE_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Metode</Label>
                <Select value={form.metode} onValueChange={(v) => setForm({ ...form, metode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{METODE_OPTIONS.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Catatan</Label><Textarea value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} placeholder="opsional" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="pembayaran-form-simpan" onClick={save} disabled={saving} className="rounded-xl">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
